import { NavLink, Outlet } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';

import { MeQuery } from '../components/guards';
import { t, type PlainKey } from '../i18n/t';
import { registerFor } from '../profile/register';

import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';

/**
 * The account area's sections, in the order a parent meets them.
 *
 * Data rather than four copies of the same JSX: the set is the only thing that
 * differs between them, and a fifth section should be one line here.
 *
 * Paths are relative, so they resolve against this route's own `/account`
 * rather than hard-coding it. `6d54852` shipped these children under a route
 * with no path at all, and an absolute `to` here would have hidden that rather
 * than broken alongside it.
 */
const SECTIONS: ReadonlyArray<{ to: string; label: PlainKey; kidOnly?: boolean }> = [
  { to: 'profile', label: 'account-tab-profile' },
  { to: 'tutor', label: 'account-tab-tutor' },
  // Kid register only. An adult's debriefs are already on
  // `/history/conversations`, inline with the list, so this section has nothing to add for them --
  // and it used to greet them with "Past sessions are available only for kid
  // register", which names a feature for a register they are not in and leaves
  // them on a dead end. `AccountHistory` redirects a typed URL for the same
  // reason.
  { to: 'history', label: 'account-tab-history', kidOnly: true },
  { to: 'settings', label: 'account-tab-settings' },
];

/**
 * `tabs-box`, not `tabs-boxed`.
 *
 * daisyUI 5 renamed it. The v4 spelling is not an error -- it is simply a
 * class nothing defines, so the tabs render as unstyled inline text with
 * nothing in any console to say why.
 */
const tab = ({ isActive }: { isActive: boolean }) => `tab flex-1 min-h-(--hit-min) ${isActive ? 'tab-active' : ''}`;

/**
 * The account area's shell.
 *
 * It was `<h1>Account</h1><Outlet/>` and nothing else: the four sections
 * existed as routes with no way to reach any of them but by typing a URL.
 * `/account` redirects to `profile`, so a parent who wanted their learner's
 * history or the tutor picker had no path at all.
 *
 * The sections keep no headings of their own. The active tab already names
 * the section, and "Account" above "Account Profile" reads as a mistake.
 */
export function AccountView() {
  // The same `me` the route guards and `AppLayout` already read, so Relay
  // serves it from the store rather than over the network.
  const data = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const register = registerFor(data.me?.learner?.defaultCharacter?.audience);
  const sections = SECTIONS.filter((s) => !s.kidOnly || register === 'kid');

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">{t('account-title')}</h1>
      {/* `tablist` is the wrong role here and is deliberately absent: these
          are links that change the URL, not tabs that swap panels in place,
          and announcing them as tabs would promise a keyboard interaction
          (arrow keys between tabs) that routing does not implement. */}
      <nav className="tabs tabs-box">
        {sections.map((section) => (
          <NavLink key={section.to} to={section.to} className={tab}>
            {t(section.label)}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
